import { StandardRequest, StandardResponse } from "./functions";
import { ILocation } from "./location";
import { IGoogleDriveConfig } from "./users";
import { ITagAssociationMeta, TagModel } from "./tag";
import { IDate } from "./date";
import { IEntityContactMeta, IEntityGroupMeta, IEntityMemoryMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "./entity-links";
import type { ContactModel } from "./contacts";
import type { GroupModel } from "./groups";
import type { MemoryModel } from "./memories";
import type { PlaceModel } from "./places";
import type { AnchorModel } from "./anchors";
import { IUnifiedFilter } from "./filters";

/**
 * Canonical media type category.
 */
export type MediaType = "image" | "video" | "audio" | "document" | "pdf" | "other";

/**
 * Determines the MediaType from a MIME content-type string and optional file name.
 * @param {string} [contentType] - The MIME content type string.
 * @param {string} [fileName] - Optional file name for extension inspection.
 * @returns {MediaType} The classified MediaType.
 */
export function getMediaType(contentType?: string, fileName?: string): MediaType {
  const lowerType = contentType?.toLowerCase()?.trim() || "";
  const lowerFile = fileName?.toLowerCase()?.trim() || "";

  if (
    lowerType.startsWith("image/") ||
    /\.(jpe?g|png|gif|webp|bmp|svg|heic|tiff?)(\?.*)?$/i.test(lowerFile)
  ) {
    return "image";
  }

  if (
    lowerType.startsWith("video/") ||
    /\.(mp4|mov|webm|m4v|mkv|avi|ogv)(\?.*)?$/i.test(lowerFile)
  ) {
    return "video";
  }

  if (
    lowerType.startsWith("audio/") ||
    /\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i.test(lowerFile)
  ) {
    return "audio";
  }

  if (
    lowerType === "application/pdf" ||
    lowerFile.endsWith(".pdf") ||
    /\.pdf(\?.*)?$/i.test(lowerFile)
  ) {
    return "pdf";
  }

  if (
    lowerType.startsWith("text/") ||
    lowerType.includes("word") ||
    lowerType.includes("document") ||
    lowerType.includes("officedocument") ||
    lowerType.includes("sheet") ||
    lowerType.includes("excel") ||
    lowerType.includes("presentation") ||
    lowerType.includes("powerpoint") ||
    lowerType === "application/rtf" ||
    lowerType === "application/msword" ||
    /\.(docx?|txt|rtf|odt|pages|csv|xlsx?|pptx?|scan|md)(\?.*)?$/i.test(lowerFile)
  ) {
    return "document";
  }

  return "other";
}

/**
 * Constant for MediaTarget when the reason a Media exists is unknown.
 */
export const MEDIA_TARGET_UNKNOWN = "unknown";

/**
 * Constant for MediaTarget when media was recorded specifically for a Memory.
 */
export const MEDIA_TARGET_MEMORY = "memory";

/**
 * Constant for MediaTarget when media was captured as a User Profile picture.
 */
export const MEDIA_TARGET_PROFILE = "profile";

/**
 * Constant for MediaTarget when media was captured as a Contact picture.
 */
export const MEDIA_TARGET_CONTACT = "contact";

/**
 * Constant for MediaTarget when media was captured as a Group picture.
 */
export const MEDIA_TARGET_GROUP = "group";

/**
 * The target/purpose describing why a Media artifact exists.
 * All target values strictly use lowercase strings.
 */
export type MediaTarget =
  | typeof MEDIA_TARGET_UNKNOWN
  | typeof MEDIA_TARGET_MEMORY
  | typeof MEDIA_TARGET_PROFILE
  | typeof MEDIA_TARGET_CONTACT
  | typeof MEDIA_TARGET_GROUP
  | (string & {});

/**
 * Constant for standard user uploaded media.
 */
export const MEDIA_CATEGORY_STANDARD = "standard";

/**
 * Constant for AI or machine generated media.
 */
export const MEDIA_CATEGORY_GENERATED = "generated";

/**
 * Constant for document, scanned paper, spreadsheet, or PDF media.
 */
export const MEDIA_CATEGORY_DOCUMENTS = "documents";

/**
 * Compatibility alias for MEDIA_CATEGORY_DOCUMENTS.
 */
export const MEDIA_CATEGORY_DOCUMENT = "documents";

/**
 * Constant for emoji icon or graphical symbol media.
 */
export const MEDIA_CATEGORY_EMOJI = "emoji";

/**
 * Constant for sticker or graphical illustration media.
 */
export const MEDIA_CATEGORY_STICKER = "sticker";

/**
 * Media category enum representing the nature or origin of the media asset.
 * All enum values strictly use lowercase strings.
 */
export type MediaCategory =
  | typeof MEDIA_CATEGORY_STANDARD
  | typeof MEDIA_CATEGORY_GENERATED
  | typeof MEDIA_CATEGORY_DOCUMENTS
  | typeof MEDIA_CATEGORY_EMOJI
  | typeof MEDIA_CATEGORY_STICKER
  | "document"
  | (string & {});

/**
 * Helper to determine if a category value is a recognized MediaCategory.
 * @param {unknown} val - Value to check.
 * @returns {boolean} True if val is recognized.
 */
export function isMediaCategory(val: unknown): val is MediaCategory {
  return (
    val === MEDIA_CATEGORY_STANDARD ||
    val === MEDIA_CATEGORY_GENERATED ||
    val === MEDIA_CATEGORY_DOCUMENTS ||
    val === "document" ||
    val === MEDIA_CATEGORY_EMOJI ||
    val === MEDIA_CATEGORY_STICKER
  );
}

/**
 * Formats a MediaCategory value into a user-friendly display string.
 * @param {string} [val] - Raw category string.
 * @returns {string} Formatted label (e.g. "Generated", "Documents", "Emoji", "Sticker", "Standard").
 */
export function formatMediaCategory(val?: string): string {
  const lower = val?.toLowerCase();
  if (lower === MEDIA_CATEGORY_GENERATED) return "Generated";
  if (lower === MEDIA_CATEGORY_DOCUMENTS || lower === "document") return "Documents";
  if (lower === MEDIA_CATEGORY_EMOJI) return "Emoji";
  if (lower === MEDIA_CATEGORY_STICKER) return "Sticker";
  return "Standard";
}

/**
 * Automatically determines the MediaCategory for an asset based on its type, content, filename, or metadata.
 *
 * @param params - Inspection parameters.
 * @param params.contentType - MIME type of the file.
 * @param params.fileName - File name or title of the media.
 * @param params.mediaType - Canonical MediaType enum if already resolved.
 * @param params.explicitCategory - Category supplied by the calling workflow (e.g. emoji picker, sticker picker, AI generator).
 * @param params.metadata - Extra metadata attributes.
 * @returns {MediaCategory} The classified lowercase MediaCategory.
 */
export function determineMediaCategory(params: {
  contentType?: string;
  fileName?: string;
  mediaType?: MediaType;
  explicitCategory?: string;
  metadata?: Record<string, any>;
}): MediaCategory {
  // 1. If an explicit valid category was provided by the caller or ingest workflow
  if (params.explicitCategory && isMediaCategory(params.explicitCategory)) {
    return params.explicitCategory === "document" ? MEDIA_CATEGORY_DOCUMENTS : params.explicitCategory;
  }

  const lowerName = (params.fileName || "").toLowerCase();
  const lowerType = (params.contentType || "").toLowerCase();
  const metaSource = (params.metadata?.source || params.metadata?.origin || "").toString().toLowerCase();

  // 2. Check for AI or machine-generated media
  if (
    metaSource === "generated" ||
    metaSource === "ai" ||
    lowerName.includes("generated_") ||
    lowerName.includes("ai_generated")
  ) {
    return MEDIA_CATEGORY_GENERATED;
  }

  // 3. Check for Emoji
  if (
    lowerName.includes("openmoji") ||
    lowerName.startsWith("emoji_") ||
    lowerName.includes("-emoji") ||
    metaSource === "emoji"
  ) {
    return MEDIA_CATEGORY_EMOJI;
  }

  // 4. Check for Sticker
  if (
    lowerName.startsWith("sticker_") ||
    lowerName.includes("-sticker") ||
    metaSource === "sticker"
  ) {
    return MEDIA_CATEGORY_STICKER;
  }

  // 5. Check for Documents / PDF / office files
  const resolvedMediaType = params.mediaType || getMediaType(params.contentType, params.fileName);
  if (
    resolvedMediaType === "document" ||
    resolvedMediaType === "pdf" ||
    lowerType === "application/pdf" ||
    lowerType.includes("word") ||
    lowerType.includes("document") ||
    lowerType.includes("sheet") ||
    lowerType.includes("presentation") ||
    /\.(pdf|docx?|txt|rtf|odt|pages|csv|xlsx?|pptx?)$/i.test(lowerName)
  ) {
    return MEDIA_CATEGORY_DOCUMENTS;
  }

  // 6. Default to standard user-uploaded media
  return MEDIA_CATEGORY_STANDARD;
}


/**
 * Metadata extracted from a media file completely offline during upload.
 * Images: EXIF date/GPS via exifr + TensorFlow MobileNet object classification.
 * Video/Audio: duration from file headers.
 */
export interface IMediaMetadata {
  /** EXIF DateTimeOriginal converted to Unix milliseconds. */
  capturedAt?: number;
  /** GPS latitude decimal degrees (from EXIF). */
  gpsLatitude?: number;
  /** GPS longitude decimal degrees (from EXIF). */
  gpsLongitude?: number;
  /** Human-readable location label (reverse-geocoded or user-supplied). */
  gpsLocationLabel?: string;
  /** Top object class labels from TensorFlow MobileNet (web only). */
  objects?: string[];
  /** Derived semantic tags (e.g. "outdoor", "animal", "portrait"). */
  tags?: string[];
  /** Image width in pixels (from EXIF). */
  width?: number;
  /** Image height in pixels (from EXIF). */
  height?: number;
  /** Duration in milliseconds for video/audio files. */
  durationMs?: number;
  /** Extracted or synthesized description (from EXIF comments or offline object classification). */
  description?: string;
  /** Canonical media category (image, video, audio, other). */
  mediaType?: MediaType;
  /** Original href, source URI, URL, or local file identifier where the media originated. Non-editable. */
  href?: string;
  /** Extensible raw metadata JSON blob for arbitrary technical attributes. */
  extraMetadata?: Record<string, any>;
  /** Unix timestamp when extraction ran on the client. */
  extractedAt?: number;
}

/**
 * @swagger
 *  components:
 *    schemas:
 *      UploadMediaRequest:
 *        type: object
 *        required: [ "base64", "title", "contentType" ]
 *        properties:
 *          base64:
 *            type: string
 *          title:
 *            type: string
 *          contentType:
 *            type: string
 */
export type UploadMediaRequest = StandardRequest & {
  base64: string;
  title: string;
  contentType: string;
  /** Optional original href, source URI, URL, or local file identifier. */
  href?: string;
  /** Optional SHA-256 binary hash of the media file. */
  fileHash?: string;
  /** Offline-extracted metadata (EXIF, TFjs objects, href). */
  metadata?: IMediaMetadata;
  /** Optional media target purpose. */
  mediaTarget?: MediaTarget;
  /** Optional target entity ID when media is tied to an entity (contactId, groupId, userId). */
  targetId?: string;
  /** Media category classification (standard, generated, emoji, sticker, documents). */
  mediaCategory?: MediaCategory;
  /** Structured location block. */
  location?: ILocation;
  /** Scene or caption description. */
  description?: string;
  /** Favourite rating on media, 1 to 5 (defaults to 3). */
  favouriteRating?: number;
  /** Existing memory ID if uploading media directly to an existing memory. */
  memoryId?: string;
  /** Explicit flag to suppress automatic Memory creation for this upload. */
  skipAutoMemory?: boolean;
  /** Issue date on certificate or document used for auto-created MemoryDate. */
  issueDate?: string | number;
  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
  /** Chronological structured date block (IDate JSON) or formatted string of the media asset. */
  date?: IDate | string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      UploadMediaResponse:
 *        type: object
 *        required: [ "downloadUrl" ]
 *        properties:
 *          downloadUrl:
 *            type: string
 *          media:
 *            $ref: '#/components/schemas/MediaModel'
 */
export type UploadMediaResponse = StandardResponse & {
  downloadUrl: string;
  media?: MediaModel;
  /** True if the media was skipped because a file with identical fileHash already exists. */
  isDuplicate?: boolean;
  /** The Firestore document ID of the pre-existing duplicate media item if duplicate. */
  existingMediaId?: string;
  /** The app navigation URL or link to the pre-existing duplicate media item if duplicate. */
  existingMediaUrl?: string;
  /** Document ID of the newly auto-created Memory if created. */
  autoCreatedMemoryId?: string;
  /** Server-side processing, safety scan, and commit latency telemetry in milliseconds. */
  serverDiagnostics?: {
    serverProcessingMs?: number;
    safetyScanMs?: number;
    virusScanMs?: number;
    storageCommitMs?: number;
    firestoreCommitMs?: number;
  };
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      MediaModel:
 *        type: object
 *        required: [ "id", "ownerId", "storagePath", "downloadUrl", "title", "contentType", "createdAt" ]
 *        properties:
 *          id:
 *            type: string
 *          ownerId:
 *            type: string
 *          storagePath:
 *            type: string
 *          downloadUrl:
 *            type: string
 *          title:
 *            type: string
 *          contentType:
 *            type: string
 *          createdAt:
 *            type: number
 *          location:
 *            type: string
 *          description:
 *            type: string
 *          memoryIds:
 *            type: array
 *            items:
 *              type: string
 */
export type Media = {
  id?: string;
  ownerId: string;
  storagePath: string;
  downloadUrl: string;
  title: string;
  contentType: string;
  mediaType?: MediaType;
  /** Purpose/target reason for media existence (defaults to MEDIA_TARGET_UNKNOWN). */
  mediaTarget?: MediaTarget;
  /** Optional target entity ID when media is tied to an entity (contactId, groupId, userId). */
  targetId?: string;
  /** Media category classification (defaults to MEDIA_CATEGORY_STANDARD = "standard"). */
  mediaCategory?: MediaCategory;
  createdAt: number;
  updatedAt?: number;
  location?: ILocation;
  description?: string;
  fileSizeBytes?: number;
  /** SHA-256 cryptographic digest of the raw uploaded binary file for integrity and deduplication. */
  fileHash?: string;
  /** Metadata extracted offline during upload (EXIF, AI objects/tags). */
  metadata?: IMediaMetadata;
  /** Favourite rating on media, 1 to 5 (defaults to 3). */
  favouriteRating?: number;
  /** Storage provider ("firebase" by default or "google_drive" when stored externally). */
  storageProvider?: "firebase" | "google_drive";
  /** External folder identifier (e.g. Google Drive folder ID) where the media file resides. */
  storageFolderId?: string;
  /** External file identifier in Google Drive. */
  driveFileId?: string;
  /** Indicates whether media is currently unavailable (e.g. external provider disconnected or changed). */
  isUnavailable?: boolean;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];
  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Companion synchronized array of contact IDs maintained for Firestore query indexing */
  contactIds?: string[];
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of group IDs maintained for Firestore query indexing */
  groupIds?: string[];
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
  /** Chronological structured date block (IDate JSON) or formatted string of the media asset. */
  date?: IDate | string;
};

/**
 * Interface alias for raw Media database document entity schema.
 */
export type IMedia = Media;

import { ShareSummary, SharedToSummary } from "./shares";

export type MediaModel = Media & {
  memoryIds?: string[];
  /**
   * Fully hydrated Memory view models associated with or containing this media asset.
   */
  memoryItems?: MemoryModel[];
  /**
   * Fully hydrated Contact view models associated with this media asset.
   */
  contactItems?: ContactModel[];
  /**
   * Fully hydrated Group view models associated with this media asset.
   */
  groupItems?: GroupModel[];
  /**
   * Fully hydrated Place view models associated with this media asset.
   */
  placeItems?: PlaceModel[];
  /**
   * Fully hydrated Anchor view models associated with this media asset.
   */
  anchorItems?: AnchorModel[];
  /**
   * Fully hydrated Tag models associated with this media asset.
   */
  tagItems?: TagModel[];
  userRole?: "owner" | "editor" | "viewer";
  canShare?: boolean;
  sharedWith?: ShareSummary[];
  sharedTo?: SharedToSummary[];
  ownerDisplayName?: string;
  /** Indicates whether media is currently unavailable (e.g. external provider disconnected or changed). */
  isUnavailable?: boolean;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyMediaRequest:
 *        type: object
 *        properties: {}
 */
export type GetMyMediaRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMyMediaResponse:
 *        type: object
 *        required: [ "media" ]
 *        properties:
 *          media:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/MediaModel'
 */
export type GetMyMediaResponse = StandardResponse & {
  media: MediaModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      EditMediaRequest:
 *        type: object
 *        required: [ "mediaId" ]
 *        properties:
 *          mediaId:
 *            type: string
 *          title:
 *            type: string
 *          location:
 *            type: object
 *          description:
 *            type: string
 *          mediaTarget:
 *            type: string
 *          metadata:
 *            type: object
 *          favouriteRating:
 *            type: integer
 *            minimum: 1
 *            maximum: 5
 *          tags:
 *            type: object
 *          date:
 *            oneOf:
 *              - type: string
 *              - type: object
 *          base64:
 *            type: string
 */
export type EditMediaRequest = StandardRequest & {
  mediaId: string;
  title?: string;
  location?: ILocation;
  description?: string;
  /** Optional base64 payload for replacement media buffer and virus scanning. */
  base64?: string;
  /** Optional updated media target purpose. */
  mediaTarget?: MediaTarget;
  /** Optional updated media category classification. */
  mediaCategory?: MediaCategory;
  /** Updated offline-extracted metadata. */
  metadata?: IMediaMetadata;
  /** Favourite rating on media, 1 to 5 (defaults to 3). */
  favouriteRating?: number;
  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];
  /** Chronological structured date block (IDate JSON) or formatted string of the media asset. */
  date?: IDate | string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      DeleteMediaRequest:
 *        type: object
 *        required: [ "mediaId" ]
 *        properties:
 *          mediaId:
 *            type: string
 */
export type DeleteMediaRequest = StandardRequest & {
  mediaId: string;
  cascadeUnlink?: boolean;
};
/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllMediaRequest:
 *        type: object
 *        properties: {}
 */
export type GetAllMediaRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetAllMediaResponse:
 *        type: object
 *        required: [ "media" ]
 *        properties:
 *          media:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/MediaModel'
 */
export type GetAllMediaResponse = StandardResponse & {
  media: MediaModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      MediaFilter:
 *        type: object
 *        properties:
 *          title:
 *            type: string
 *            description: Regex pattern to filter media by title
 *          url:
 *            type: string
 *            description: Regex pattern to filter media by download URL
 */
export type MediaFilter = {
  title?: string;
  url?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMediaRequest:
 *        type: object
 *        properties:
 *          filter:
 *            $ref: '#/components/schemas/MediaFilter'
 */
export type GetMediaRequest = StandardRequest & {
  mediaId?: string;
  filter?: MediaFilter | IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMediaResponse:
 *        type: object
 *        required: [ "status" ]
 *        properties:
 *          status:
 *            type: boolean
 *          media:
 *            oneOf:
 *              - $ref: '#/components/schemas/MediaModel'
 *              - type: array
 *                items:
 *                  $ref: '#/components/schemas/MediaModel'
 *          error:
 *            type: string
 */
export type GetMediaResponse = StandardResponse & {
  media?: MediaModel | MediaModel[];
};

/**
 * Default storage quota per user in bytes (2 GB).
 */
export const DEFAULT_STORAGE_QUOTA_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * Aggregated user storage capacity and item count statistics.
 */
export interface IStorageStats {
  ownerId: string;
  totalSizeBytes: number;
  mediaCount: number;
  updatedAt: number;
}


/**
 * Summary of a user's storage quota, consumed capacity, and remaining bytes.
 */
export interface IUserStorageQuotaSummary {
  ownerId: string;
  storageQuotaBytes: number;
  usedBytes: number;
  remainingBytes: number;
  usagePercentage: number;
  mediaCount: number;
  updatedAt: number;
}

export type GetUserStorageQuotaRequest = StandardRequest & {
  uid?: string;
};

export type GetUserStorageQuotaResponse = StandardResponse & {
  quota?: IUserStorageQuotaSummary;
};

export type ConnectGoogleDriveStorageRequest = StandardRequest & {
  folderId: string;
  folderName: string;
};

export type ConnectGoogleDriveStorageResponse = StandardResponse & {
  storageProvider: "google_drive";
  googleDriveConfig: IGoogleDriveConfig;
};

export type DisconnectGoogleDriveStorageRequest = StandardRequest & {};

export type DisconnectGoogleDriveStorageResponse = StandardResponse & {
  storageProvider: "firebase";
};

